/**
 * Email HTML templejti — branded za Infinity Creative Agency.
 *
 * Sve table-based (radi u Outlook / Gmail / Apple Mail / mobilni klijenti),
 * inline CSS samo (eksterni stylesheet email klijenti uglavnom strip-uju).
 *
 * Dizajn: bijela pozadina, narandžasti accenti (brand), crna tipografija.
 */

const BRAND = {
  primary: '#f97316',     // orange-500
  primaryDark: '#ea580c', // orange-600
  text: '#0f172a',        // slate-900
  textMuted: '#64748b',   // slate-500
  border: '#e5e7eb',      // gray-200
  bg: '#f8fafc',          // slate-50
  white: '#ffffff',
};

const SITE_URL = 'https://infinitycreative.agency';
const LOGO_URL = `${SITE_URL}/logo512.png`;

const escapeHtml = (str = '') =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const nl2br = (str = '') => escapeHtml(str).replace(/\n/g, '<br/>');

// =========================================================================
// LAYOUT WRAPPER — sve emailove uvija u istu branded "ljusku".
// =========================================================================

/**
 * @param {object} params
 * @param {string} params.preview      Hidden preview text (Gmail snippet)
 * @param {string} params.title        Glavni naslov u headeru (npr. "Nova ponuda")
 * @param {string} [params.eyebrow]    Mala oznaka iznad naslova (npr. "POTVRDA")
 * @param {string} params.bodyHtml     Glavni HTML sadržaj (između headera i footera)
 * @param {object} [params.cta]                  Optional CTA button: { url, label }
 * @param {string} [params.footerNote]           Dodatni mali tekst u footeru
 * @param {string|string[]} [params.notificationRecipient]
 *   Adresa(e) na koju je notifikacija isporučena — prikazuje se ispod footera
 *   kao "Notifikacija isporučena na: ...". Korisno za admin notifikacije gdje
 *   želiš vidjeti na koju od više adresa je email otišao.
 */
export const layout = ({
  preview = '',
  title,
  eyebrow = '',
  bodyHtml,
  cta = null,
  footerNote = '',
  notificationRecipient = '',
}) => {
  const recipientList = Array.isArray(notificationRecipient)
    ? notificationRecipient.filter(Boolean).join(', ')
    : (notificationRecipient || '');

  return `<!DOCTYPE html>
<html lang="bs">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
</head>
<body style="margin:0;padding:0;background:${BRAND.bg};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:${BRAND.text};-webkit-font-smoothing:antialiased;">
  <!-- Hidden preview text -->
  <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:${BRAND.bg};opacity:0;">
    ${escapeHtml(preview || title)}
  </div>

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${BRAND.bg};padding:32px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:${BRAND.white};border-radius:20px;overflow:hidden;box-shadow:0 4px 24px rgba(15,23,42,0.06);">

          <!-- Header / Brand bar -->
          <tr>
            <td style="background:${BRAND.text};padding:28px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="vertical-align:middle;">
                    <a href="${SITE_URL}" style="text-decoration:none;color:${BRAND.white};display:inline-block;">
                      <span style="font-size:18px;font-weight:900;letter-spacing:0.4px;">INFINITY</span>
                      <span style="font-size:18px;font-weight:300;letter-spacing:0.4px;">CREATIVE</span>
                    </a>
                  </td>
                  <td align="right" style="vertical-align:middle;">
                    <span style="display:inline-block;padding:6px 12px;background:${BRAND.primary};color:${BRAND.white};font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;border-radius:999px;">
                      Notification
                    </span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Title block -->
          <tr>
            <td style="padding:36px 32px 8px 32px;">
              ${eyebrow ? `<div style="font-size:11px;font-weight:800;color:${BRAND.primary};text-transform:uppercase;letter-spacing:2px;margin-bottom:12px;">${escapeHtml(eyebrow)}</div>` : ''}
              <h1 style="margin:0;font-size:26px;line-height:1.25;font-weight:800;letter-spacing:-0.4px;color:${BRAND.text};">
                ${escapeHtml(title)}
              </h1>
            </td>
          </tr>

          <!-- Body content -->
          <tr>
            <td style="padding:24px 32px 16px 32px;font-size:15px;line-height:1.65;color:${BRAND.text};">
              ${bodyHtml}
            </td>
          </tr>

          ${cta ? `
          <!-- CTA Button -->
          <tr>
            <td align="center" style="padding:8px 32px 36px 32px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="background:${BRAND.primary};border-radius:12px;">
                    <a href="${cta.url}" style="display:inline-block;padding:14px 32px;font-size:13px;font-weight:800;color:${BRAND.white};text-decoration:none;text-transform:uppercase;letter-spacing:1.5px;">
                      ${escapeHtml(cta.label)}
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ` : `<tr><td style="padding:0 32px 24px 32px;"></td></tr>`}

          <!-- Footer -->
          <tr>
            <td style="padding:24px 32px 32px 32px;border-top:1px solid ${BRAND.border};background:#fafafa;">
              ${footerNote ? `<p style="margin:0 0 12px 0;font-size:12px;color:${BRAND.textMuted};line-height:1.6;">${footerNote}</p>` : ''}
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="font-size:11px;color:${BRAND.textMuted};line-height:1.6;">
                    <strong style="color:${BRAND.text};">Infinity Creative Agency</strong><br/>
                    Tuzla, Bosna i Hercegovina<br/>
                    <a href="mailto:info@infinitycreative.agency" style="color:${BRAND.primary};text-decoration:none;">info@infinitycreative.agency</a>
                    &nbsp;·&nbsp;
                    <a href="${SITE_URL}" style="color:${BRAND.primary};text-decoration:none;">infinitycreative.agency</a>
                  </td>
                </tr>
                ${recipientList ? `
                <tr>
                  <td style="padding-top:14px;">
                    <div style="display:inline-block;padding:8px 14px;background:#fff;border:1px solid ${BRAND.border};border-radius:8px;font-size:11px;color:${BRAND.textMuted};line-height:1.5;">
                      <span style="font-weight:700;color:${BRAND.text};text-transform:uppercase;letter-spacing:1px;font-size:10px;">Isporučeno na:</span>
                      &nbsp;
                      <span style="color:${BRAND.primary};font-weight:600;">${escapeHtml(recipientList)}</span>
                    </div>
                  </td>
                </tr>
                ` : ''}
              </table>
            </td>
          </tr>

        </table>

        <!-- Sub-footer -->
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;margin-top:16px;">
          <tr>
            <td align="center" style="font-size:10px;color:${BRAND.textMuted};font-weight:600;letter-spacing:1px;text-transform:uppercase;">
              © ${new Date().getFullYear()} Infinity Creative Agency · Automatska notifikacija
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// =========================================================================
// REUSABLE BUILDING BLOCKS — koriste se unutar bodyHtml
// =========================================================================

/**
 * Info row — labela + vrijednost u 2 kolone.
 *
 * @param {string} label
 * @param {string|number|{ html: string }} value
 *   - string/number → automatski HTML-escape (bezbjedno za sve user input)
 *   - { html: '...' } → sirovi HTML (koristi se za link-ove, <strong>, itd.)
 */
export const infoRow = (label, value) => {
  if (value === null || value === undefined || value === '') return '';

  let displayValue;
  if (typeof value === 'object' && value !== null && typeof value.html === 'string') {
    displayValue = value.html;
  } else {
    displayValue = escapeHtml(String(value));
  }

  return `
    <tr>
      <td style="padding:10px 0;border-bottom:1px solid ${BRAND.border};font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1px;width:35%;vertical-align:top;">
        ${escapeHtml(label)}
      </td>
      <td style="padding:10px 0;border-bottom:1px solid ${BRAND.border};font-size:14px;color:${BRAND.text};font-weight:600;word-break:break-word;">
        ${displayValue}
      </td>
    </tr>`;
};

/** Wrap info rows in a table */
export const infoTable = (rows) => `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 16px 0;">
    ${rows.filter(Boolean).join('')}
  </table>`;

/**
 * Highlighted box (npr. za PIN code, ili za "Klijent je odabrao paket").
 *
 * @param {object} params
 * @param {string} params.label
 * @param {string|{ html: string }} params.value
 * @param {string} [params.color]
 */
export const highlightBox = ({ label, value, color = BRAND.primary }) => {
  let displayValue;
  if (typeof value === 'object' && value !== null && typeof value.html === 'string') {
    displayValue = value.html;
  } else {
    displayValue = escapeHtml(String(value ?? ''));
  }
  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0;">
    <tr>
      <td style="background:#fff7ed;border:1px solid ${color}33;border-left:4px solid ${color};padding:18px 22px;border-radius:10px;">
        <div style="font-size:11px;font-weight:800;color:${color};text-transform:uppercase;letter-spacing:1.5px;margin-bottom:6px;">
          ${escapeHtml(label)}
        </div>
        <div style="font-size:18px;font-weight:800;color:${BRAND.text};line-height:1.4;">
          ${displayValue}
        </div>
      </td>
    </tr>
  </table>`;
};

/** Lista stavki (npr. odabrani paketi) */
export const itemList = (items) => {
  if (!items || items.length === 0) return '';
  return `
    <ul style="margin:8px 0 16px 0;padding:0 0 0 20px;font-size:14px;color:${BRAND.text};line-height:1.7;">
      ${items.map(it => `<li style="margin-bottom:4px;">${escapeHtml(String(it))}</li>`).join('')}
    </ul>`;
};

// =========================================================================
// PRE-BUILT TEMPLATES — funkcije koje vraćaju { subject, html, text_alt }
// =========================================================================

// ───────────────────────────────────────────────────────────────
// CONTACT FORM
// ───────────────────────────────────────────────────────────────

/** Email adminu kad neko popuni kontakt formu */
export const contactReceivedAdmin = ({ name, email, phone, weddingDate, message, packageName, language, contactMethods, notificationRecipient }) => {
  const subject = `Nova poruka sa web stranice — ${name}`;
  const body = `
    <p style="margin:0 0 16px 0;">Stigla je nova poruka sa kontakt forme.</p>

    ${infoTable([
      infoRow('Ime', name),
      infoRow('Email', { html: `<a href="mailto:${escapeHtml(email)}" style="color:${BRAND.primary};text-decoration:none;font-weight:600;">${escapeHtml(email)}</a>` }),
      infoRow('Telefon', phone || '/'),
      infoRow('Datum vjenčanja', weddingDate || '/'),
      infoRow('Paket', packageName || '/'),
      infoRow('Preferirani kanal', (contactMethods && contactMethods.length) ? contactMethods.join(', ') : '/'),
      infoRow('Jezik', language === 'bs' ? 'Bosanski' : 'Engleski'),
    ])}

    <div style="margin-top:24px;font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">
      Poruka
    </div>
    <div style="padding:18px 22px;background:#f8fafc;border-radius:10px;font-size:14px;line-height:1.65;color:${BRAND.text};">
      ${nl2br(message)}
    </div>
  `;
  return {
    subject,
    html: layout({
      preview: `Nova poruka od ${name}`,
      eyebrow: 'NOVA PORUKA',
      title: name,
      bodyHtml: body,
      cta: { url: 'https://infinitycreative.agency/admin/messages', label: 'Otvori u Admin Panelu' },
      footerNote: 'Možeš odgovoriti direktno na ovaj email — Reply-To je postavljen na adresu klijenta.',
      notificationRecipient,
    }),
  };
};

/** Email klijentu — potvrda da je poruka primljena */
export const contactReceivedClient = ({ name, language = 'bs' }) => {
  const isBs = language === 'bs';
  const subject = isBs ? 'Hvala što ste nas kontaktirali' : 'Thank you for reaching out';
  const greeting = isBs ? `Dragi/a ${escapeHtml(name)},` : `Dear ${escapeHtml(name)},`;
  const main = isBs
    ? 'Vaša poruka je uspješno primljena. Javit ćemo Vam se u najkraćem mogućem roku — najčešće u roku od 24 sata.'
    : 'Your message has been received. We will get back to you as soon as possible — usually within 24 hours.';
  const sign = isBs ? 'Tim Infinity Creative Agency' : 'The Infinity Creative Agency Team';

  const body = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 20px 0;color:${BRAND.text};">${main}</p>
    <p style="margin:24px 0 0 0;font-weight:700;">${sign}</p>
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: isBs ? 'POTVRDA PRIJEMA' : 'MESSAGE RECEIVED',
      title: isBs ? 'Hvala što ste nam pisali' : 'Thank you for your message',
      bodyHtml: body,
      cta: { url: 'https://infinitycreative.agency', label: isBs ? 'Posjeti web stranicu' : 'Visit our website' },
    }),
  };
};

// ───────────────────────────────────────────────────────────────
// OFFER LIFECYCLE
// ───────────────────────────────────────────────────────────────

/** Email klijentu kad admin kreira/pošalje novu ponudu */
export const offerSentClient = ({ clientName, offerUrl, packageName, datum, valuta, total, language = 'bs' }) => {
  const isBs = language === 'bs';
  const subject = isBs
    ? `Vaša ponuda od Infinity Creative Agency`
    : `Your offer from Infinity Creative Agency`;

  const body = `
    <p style="margin:0 0 12px 0;">${isBs ? `Poštovani/a ${escapeHtml(clientName)},` : `Dear ${escapeHtml(clientName)},`}</p>
    <p style="margin:0 0 20px 0;">${isBs
      ? 'Pripremili smo personalizovanu ponudu za Vas. Možete je pregledati klikom na dugme ispod.'
      : 'We have prepared a personalized offer for you. You can review it by clicking the button below.'}</p>

    ${infoTable([
      infoRow(isBs ? 'Paket' : 'Package', packageName || '/'),
      datum ? infoRow(isBs ? 'Datum događaja' : 'Event date', datum) : '',
      total ? infoRow(isBs ? 'Iznos' : 'Total', `${total} ${valuta || 'BAM'}`) : '',
    ].filter(Boolean))}

    <p style="margin:24px 0 0 0;font-size:13px;color:${BRAND.textMuted};">${isBs
      ? 'U ponudi možete odabrati željeni paket, dodatne usluge i potvrditi rezervaciju online.'
      : 'In the offer you can select your preferred package, add-ons, and confirm the booking online.'}</p>
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: isBs ? 'NOVA PONUDA' : 'NEW OFFER',
      title: isBs ? 'Vaša personalizovana ponuda' : 'Your personalized offer',
      bodyHtml: body,
      cta: { url: offerUrl, label: isBs ? 'Pregledaj ponudu' : 'Review offer' },
      footerNote: isBs
        ? 'Ako imate pitanja, jednostavno odgovorite na ovaj email.'
        : 'If you have any questions, simply reply to this email.',
    }),
  };
};

/** Email adminu kad klijent prihvati ponudu */
export const offerAcceptedAdmin = ({
  clientName, packageName, datum, total, valuta,
  selectedMainItems = [], selectedExtras = [],
  privacyEnabled = false, privacyPrice, privacyCurrency,
  guestGalleryEnabled = false, guestGalleryPrice, guestGalleryCurrency,
  pinCode, offerUrl, notificationRecipient,
}) => {
  const subject = `✅ Ponuda PRIHVAĆENA — ${clientName}`;

  const itemsList = selectedMainItems.length > 0
    ? itemList(selectedMainItems.map(i => i.name || String(i)))
    : '';
  const extrasList = selectedExtras.length > 0
    ? itemList(selectedExtras.map(e => {
        const naziv = typeof e.naziv === 'object' ? (e.naziv.bs || e.naziv.en) : e.naziv;
        return naziv;
      }))
    : '';

  const body = `
    <p style="margin:0 0 16px 0;">Klijent je <strong style="color:#10b981;">prihvatio ponudu</strong>.</p>

    ${infoTable([
      infoRow('Klijent', clientName),
      infoRow('Datum', datum),
      infoRow('Glavni paket', packageName),
      infoRow('Ukupan iznos', { html: `<strong style="color:${BRAND.primary};font-size:16px;">${escapeHtml(String(total))} ${escapeHtml(String(valuta))}</strong>` }),
    ])}

    ${selectedMainItems.length > 0 ? `
      <div style="margin-top:20px;font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1.5px;">Odabrani paketi</div>
      ${itemsList}
    ` : ''}

    ${selectedExtras.length > 0 ? `
      <div style="margin-top:8px;font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1.5px;">Dodatne usluge</div>
      ${extrasList}
    ` : ''}

    ${privacyEnabled ? highlightBox({
      label: 'Privatnost zatražena',
      value: `+${String(privacyPrice || '')} ${String(privacyCurrency || '')}`,
    }) : ''}

    ${guestGalleryEnabled ? highlightBox({
      label: 'QR galerija za goste zatražena',
      value: `+${String(guestGalleryPrice || '')} ${String(guestGalleryCurrency || '')}`,
    }) : ''}

    ${pinCode ? highlightBox({
      label: 'Generisan PIN za planiranje',
      value: { html: `<span style="font-family:'SF Mono',Menlo,monospace;font-size:24px;letter-spacing:4px;">${escapeHtml(pinCode)}</span>` },
    }) : ''}
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: 'PONUDA PRIHVAĆENA',
      title: clientName,
      bodyHtml: body,
      cta: offerUrl ? { url: offerUrl, label: 'Otvori ponudu' } : null,
      notificationRecipient,
    }),
  };
};

/** Email adminu kad klijent odbije ponudu */
export const offerRejectedAdmin = ({ clientName, packageName, datum, reason, offerUrl, notificationRecipient }) => {
  const subject = `❌ Ponuda ODBIJENA — ${clientName}`;
  const body = `
    <p style="margin:0 0 16px 0;">Klijent je <strong style="color:#ef4444;">odbio ponudu</strong>.</p>

    ${infoTable([
      infoRow('Klijent', clientName),
      infoRow('Datum', datum),
      infoRow('Paket', packageName),
    ])}

    ${reason ? highlightBox({
      label: 'Razlog odbijanja',
      value: reason,
      color: '#ef4444',
    }) : ''}
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: 'PONUDA ODBIJENA',
      title: clientName,
      bodyHtml: body,
      cta: offerUrl ? { url: offerUrl, label: 'Otvori ponudu' } : null,
      notificationRecipient,
    }),
  };
};

/** Email klijentu — potvrda nakon prihvatanja ponude */
export const offerAcceptedClient = ({ clientName, packageName, datum, total, valuta, pinCode, offerUrl, language = 'bs' }) => {
  const isBs = language === 'bs';
  const subject = isBs
    ? `Hvala — Vaša ponuda je potvrđena!`
    : `Thank you — your booking is confirmed!`;

  const body = `
    <p style="margin:0 0 16px 0;">${isBs ? `Poštovani/a ${escapeHtml(clientName)},` : `Dear ${escapeHtml(clientName)},`}</p>
    <p style="margin:0 0 20px 0;">${isBs
      ? 'Hvala Vam što ste se odlučili za nas. Vaša rezervacija je <strong>uspješno potvrđena</strong>. Radujemo se saradnji!'
      : 'Thank you for choosing us. Your booking has been <strong>successfully confirmed</strong>. We are looking forward to working with you!'}</p>

    ${infoTable([
      infoRow(isBs ? 'Paket' : 'Package', packageName),
      infoRow(isBs ? 'Datum' : 'Date', datum),
      infoRow(isBs ? 'Iznos' : 'Total', { html: `<strong style="color:${BRAND.primary};">${escapeHtml(String(total))} ${escapeHtml(String(valuta))}</strong>` }),
    ])}

    ${pinCode ? highlightBox({
      label: isBs ? 'Vaš PIN za pristup planiranju' : 'Your planning access PIN',
      value: { html: `<span style="font-family:'SF Mono',Menlo,monospace;font-size:28px;letter-spacing:6px;">${escapeHtml(pinCode)}</span>` },
    }) : ''}

    <p style="margin:24px 0 0 0;font-size:13px;color:${BRAND.textMuted};">${isBs
      ? 'PIN čuvajte na sigurnom — koristit ćete ga za pristup kutku za planiranje gdje možete unijeti satnicu, kontakte i napomene.'
      : 'Keep this PIN safe — you will use it to access the planning area where you can input the timeline, contacts, and notes.'}</p>
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: isBs ? 'POTVRDA REZERVACIJE' : 'BOOKING CONFIRMED',
      title: isBs ? 'Rezervacija potvrđena' : 'Booking confirmed',
      bodyHtml: body,
      cta: offerUrl ? { url: offerUrl, label: isBs ? 'Otvori ponudu' : 'Open offer' } : null,
    }),
  };
};

/** Email adminu kad klijent ažurira planning podatke */
export const planningUpdatedAdmin = ({ clientName, packageName, timeline = [], contactPhones = [], notes, offerUrl, notificationRecipient }) => {
  const subject = `🔄 Klijent je ažurirao planiranje — ${clientName}`;

  const timelineHtml = timeline.length > 0
    ? `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 16px 0;">
        ${timeline.map(t => `
          <tr>
            <td style="padding:8px 0;border-bottom:1px dashed ${BRAND.border};width:90px;font-family:'SF Mono',Menlo,monospace;font-size:13px;font-weight:700;color:${BRAND.primary};">
              ${escapeHtml(t.time || '--:--')}
            </td>
            <td style="padding:8px 0 8px 12px;border-bottom:1px dashed ${BRAND.border};font-size:14px;color:${BRAND.text};">
              ${escapeHtml(t.event || '')}
            </td>
          </tr>
        `).join('')}
      </table>`
    : `<p style="margin:0;color:${BRAND.textMuted};font-style:italic;">Nema unesenog timeline-a.</p>`;

  const contactsHtml = contactPhones.length > 0
    ? itemList(contactPhones.filter(c => c.name || c.phone).map(c => `${c.name}: ${c.phone}`))
    : `<p style="margin:0;color:${BRAND.textMuted};font-style:italic;">Nema unesenih kontakata.</p>`;

  const body = `
    <p style="margin:0 0 16px 0;">Klijent je dopunio podatke za planiranje.</p>

    ${infoTable([
      infoRow('Klijent', clientName),
      infoRow('Paket', packageName),
    ])}

    <div style="margin-top:24px;font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">Satnica</div>
    ${timelineHtml}

    <div style="margin-top:8px;font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">Kontakti</div>
    ${contactsHtml}

    ${notes ? `
      <div style="margin-top:16px;font-size:12px;font-weight:700;color:${BRAND.textMuted};text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">Napomene</div>
      <div style="padding:14px 18px;background:#f8fafc;border-radius:8px;font-size:14px;line-height:1.65;">${nl2br(notes)}</div>
    ` : ''}
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: 'AŽURIRANJE PLANIRANJA',
      title: clientName,
      bodyHtml: body,
      cta: offerUrl ? { url: offerUrl, label: 'Pregledaj ponudu' } : null,
      notificationRecipient,
    }),
  };
};

// ───────────────────────────────────────────────────────────────
// ZAHTJEV ZA RECENZIJU — šalje se klijentu nakon isporuke fotografija
// ───────────────────────────────────────────────────────────────

export const reviewRequestClient = ({ clientName, reviewUrl, galleryUrl, language = 'bs' }) => {
  const isBs = language === 'bs';
  const subject = isBs
    ? 'Možete li nam ostaviti recenziju? 🙏'
    : 'Could you leave us a review? 🙏';

  const body = `
    <p style="margin:0 0 16px 0;">${isBs ? `Dragi/a ${escapeHtml(clientName)},` : `Dear ${escapeHtml(clientName)},`}</p>
    <p style="margin:0 0 20px 0;">${isBs
      ? 'Hvala Vam još jednom što ste nam ukazali povjerenje! Vaši su podaci i fotografije isporučeni, a nama bi <strong>zaista mnogo značila</strong> Vaša kratka recenzija.'
      : 'Thank you once again for trusting us! Your photos have been delivered, and a short review from you would <strong>mean the world to us</strong>.'}</p>
    ${galleryUrl ? `
    <p style="margin:0 0 20px 0;">${isBs
      ? `Ako želite još jednom pogledati fotografije, galerija je dostupna <a href="${escapeHtml(galleryUrl)}" style="color:${BRAND.primary};font-weight:600;">ovdje</a>.`
      : `If you would like to revisit your photos, the gallery is available <a href="${escapeHtml(galleryUrl)}" style="color:${BRAND.primary};font-weight:600;">here</a>.`}</p>` : ''}
    <p style="margin:0 0 8px 0;font-size:13px;color:${BRAND.textMuted};">${isBs
      ? 'Recenziju možete ostaviti klikom na dugme ispod:'
      : 'You can leave a review by clicking the button below:'}</p>
  `;

  return {
    subject,
    html: layout({
      preview: subject,
      eyebrow: isBs ? 'RECENZIJA' : 'REVIEW',
      title: isBs ? 'Kako je prošla Vaša saradnja?' : 'How was your experience?',
      bodyHtml: body,
      cta: reviewUrl ? { url: reviewUrl, label: isBs ? 'Ostavi recenziju' : 'Leave a review' } : null,
    }),
  };
};

// ───────────────────────────────────────────────────────────────
// TEST EMAIL — koristi se iz AdminSettings za "Pošalji test"
// ───────────────────────────────────────────────────────────────

export const testEmail = ({ recipientName = '', notificationRecipient = '' } = {}) => {
  const subject = 'Test email iz Infinity admin panela';
  const body = `
    <p style="margin:0 0 16px 0;">${recipientName ? `Dragi/a ${escapeHtml(recipientName)},` : 'Pozdrav!'}</p>
    <p style="margin:0 0 16px 0;">Ovo je <strong>test email</strong> poslan iz Infinity Creative Agency admin panela.</p>
    <p style="margin:0 0 16px 0;">Ako vidite ovaj email — SMTP je <strong style="color:#10b981;">ispravno konfigurisan</strong> i sve buduće notifikacije će raditi normalno.</p>
    ${highlightBox({
      label: 'Status SMTP-a',
      value: '✓ Konekcija uspješna · Email isporučen',
      color: '#10b981',
    })}
  `;
  return {
    subject,
    html: layout({
      preview: 'Test email iz admin panela',
      eyebrow: 'SMTP TEST',
      title: 'Test email',
      bodyHtml: body,
      notificationRecipient,
    }),
  };
};
